/**
 * Group-by-project helpers для worker-flow: используются на read-only сводке
 * (FieldTimesheetPage при status=signed/approved) и на экране подписи
 * (FieldSignPage). Чистые функции, без зависимостей от React.
 */
import type { Project, TimesheetEntry, WorkType } from "@/api/field-timesheet";

export interface EntryRow {
  id: string;
  workType: string | null;
  hoursHalf: number;
}

export interface ProjectGroup {
  projectKey: string;
  code: string;
  name: string | null;
  isRaw: boolean;
  totalHalf: number;
  rows: ReadonlyArray<EntryRow>;
}

export function fmtH(hoursHalf: number): string {
  const v = hoursHalf / 2;
  return Number.isInteger(v) ? `${v} ч` : `${v.toFixed(1)} ч`;
}

export function buildProjectMap(projects: readonly Project[]): Map<number, Project> {
  const m = new Map<number, Project>();
  for (const p of projects) {
    if (p.id !== null && p.id !== undefined) m.set(p.id, p);
  }
  return m;
}

export function buildWorkTypeCatalogs(items: readonly WorkType[]): {
  workTypeNameById: Map<number, string>;
} {
  const workTypeNameById = new Map<number, string>();
  for (const item of items) {
    if (item.id !== null && item.id !== undefined) {
      workTypeNameById.set(item.id, item.name);
    }
  }
  return { workTypeNameById };
}

export function workTypeLabel(
  e: TimesheetEntry,
  workTypeNameById: Map<number, string>,
): string | null {
  if (e.work_type_text) return e.work_type_text;
  if (e.work_type_id !== null && e.work_type_id !== undefined) {
    return workTypeNameById.get(e.work_type_id) ?? null;
  }
  return null;
}

export function groupEntriesByProject(
  entries: readonly TimesheetEntry[],
  projectsById: Map<number, Project>,
  workTypeNameById: Map<number, string>,
): ProjectGroup[] {
  const buckets = new Map<string, ProjectGroup>();
  for (const e of entries) {
    const key = entryKey(e);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        projectKey: key,
        ...resolveProjectLabel(e, projectsById),
        totalHalf: 0,
        rows: [],
      };
      buckets.set(key, bucket);
    }
    bucket.totalHalf += e.hours_half;
    (bucket.rows as EntryRow[]).push({
      id:
        e.id !== null && e.id !== undefined
          ? `${e.id}`
          : `${e.timesheet_id}-${e.created_at ?? Math.random()}`,
      workType: workTypeLabel(e, workTypeNameById),
      hoursHalf: e.hours_half,
    });
  }
  return [...buckets.values()].sort((a, b) => {
    const aReal = !a.isRaw && a.projectKey !== "__none__";
    const bReal = !b.isRaw && b.projectKey !== "__none__";
    if (aReal !== bReal) return aReal ? -1 : 1;
    return b.totalHalf - a.totalHalf;
  });
}

function entryKey(e: TimesheetEntry): string {
  if (e.project_id !== null && e.project_id !== undefined) return `id:${e.project_id}`;
  if (e.project_code_raw) return `raw:${e.project_code_raw}`;
  return "__none__";
}

function resolveProjectLabel(
  e: TimesheetEntry,
  projectsById: Map<number, Project>,
): Pick<ProjectGroup, "code" | "name" | "isRaw"> {
  if (e.project_code_raw) {
    return { code: e.project_code_raw, name: null, isRaw: true };
  }
  if (e.project_id !== null && e.project_id !== undefined) {
    const p = projectsById.get(e.project_id);
    return {
      code: p?.code ?? `№ ${e.project_id}`,
      name: p?.name ?? null,
      isRaw: false,
    };
  }
  return { code: "—", name: null, isRaw: false };
}
